import { Gavel, ShieldAlert, Swords } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { GlassCard, Reveal, RevealItem, RevealList, SlideShell } from "@/ui";

const CARDS = [
  {
    icon: ShieldAlert,
    tone: "amber",
    title: "Spam systems",
    body: (
      <>
        Search quality and spam systems can limit sites with{" "}
        <span className="font-semibold text-white">thin, misleading, or scaled low-value</span>{" "}
        content.
      </>
    ),
  },
  {
    icon: Gavel,
    tone: "rose",
    title: "Manual actions & security",
    body: (
      <>
        Manual actions and security issues (hacks, spam injection) can{" "}
        <span className="font-semibold text-white">remove or suppress</span> visibility until
        fixed.
      </>
    ),
  },
  {
    icon: Swords,
    tone: "rose",
    title: "Negative SEO",
    body: (
      <>
        Usually means <span className="font-semibold text-white">harmful off-site actions</span>{" "}
        — toxic link spam, fake reviews — aimed at a target.
      </>
    ),
  },
] as const;

const TONE = {
  amber: {
    icon: "text-accent-amber",
    box: "border-accent-amber/25 bg-accent-amber/10 group-hover:border-accent-amber/50",
  },
  rose: {
    icon: "text-accent-rose",
    box: "border-accent-rose/25 bg-accent-rose/10 group-hover:border-accent-rose/50",
  },
} as const;

export function TrustRisksSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={1} title="What can hurt trust and rankings">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-10">
        <RevealList active={active} stagger={0.18} delay={0.25} className="grid grid-cols-3 gap-8">
          {CARDS.map(({ icon: Icon, tone, title, body }) => (
            <RevealItem key={title} className="h-full">
              <GlassCard className="group flex h-full flex-col gap-5 p-8 transition-transform duration-300 hover:-translate-y-1">
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-xl border transition-colors duration-300 ${TONE[tone].box}`}
                >
                  <Icon className={`h-7 w-7 ${TONE[tone].icon}`} strokeWidth={2} />
                </div>
                <h3 className="font-display text-3xl font-semibold text-white">{title}</h3>
                <p className="text-2xl leading-relaxed text-white/80">{body}</p>
              </GlassCard>
            </RevealItem>
          ))}
        </RevealList>

        {/* Rose-tinted footer strip */}
        <Reveal active={active} delay={1.1} y={20}>
          <div className="rounded-2xl border border-accent-rose/25 bg-accent-rose/10 px-10 py-6 text-center">
            <p className="text-2xl leading-relaxed text-white/85">
              We cover <span className="font-semibold text-white">recognition and defense</span>{" "}
              later — not tactics to attack others.
            </p>
          </div>
        </Reveal>
      </div>
    </SlideShell>
  );
}
